import axiosInstance from './axiosInstance';

export interface AnalyticsData {
  basicStats: {
    totalOrders: number;
    todayOrders: number;
    deliveredOrders: number;
    inTransitOrders: number;
    pendingOrders: number;
    failedOrders: number;
    totalRevenue: number;
  };
  recentOrders: any[];
  deliveryInsights: any[];
  medicationCategories: any[];
  patientDemographics: any[];
  revenueData: any[];
  orderStatus: any[];
  satisfactionData: any[];
}

class AnalyticsService {
  private baseEndpoint = '/analytics';

  /**
   * Get all analytics data
   */
  async getAllAnalytics(): Promise<AnalyticsData> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/all`);
      return response.data?.data || this.getEmptyAnalytics();
    } catch (error) {
      console.error('Error fetching analytics:', error);
      return this.getEmptyAnalytics();
    }
  }

  /**
   * Get delivery insights
   */
  async getDeliveryInsights(): Promise<any[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/delivery-insights`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching delivery insights:', error);
      return [];
    }
  }

  /**
   * Get patient demographics
   */
  async getPatientDemographics(): Promise<any[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/patient-demographics`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching patient demographics:', error);
      return [];
    }
  }

  /**
   * Get revenue data
   */
  async getRevenueData(): Promise<any[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/revenue-data`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      return [];
    }
  }

  /**
   * Get order status data
   */
  async getOrderStatusData(): Promise<any[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/order-status`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching order status data:', error);
      return [];
    }
  }

  /**
   * Get medication categories
   */
  async getMedicationCategories(): Promise<any[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/medication-categories`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching medication categories:', error);
      return [];
    }
  }

  /**
   * Get satisfaction data
   */
  async getSatisfactionData(): Promise<any[]> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/satisfaction-data`);
      return response.data?.data || [];
    } catch (error) {
      console.error('Error fetching satisfaction data:', error);
      return [];
    }
  }

  /**
   * Export analytics report as PDF
   */
  async exportAnalyticsReport(): Promise<Blob> {
    try {
      const response = await axiosInstance.get(`${this.baseEndpoint}/export`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting analytics report:', error);
      throw error;
    }
  }

  private getEmptyAnalytics(): AnalyticsData {
    return {
      basicStats: {
        totalOrders: 0,
        todayOrders: 0,
        deliveredOrders: 0,
        inTransitOrders: 0,
        pendingOrders: 0,
        failedOrders: 0,
        totalRevenue: 0
      },
      recentOrders: [],
      deliveryInsights: [],
      medicationCategories: [],
      patientDemographics: [],
      revenueData: [],
      orderStatus: [],
      satisfactionData: []
    };
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

// Export convenience functions
export const getAllAnalytics = () => analyticsService.getAllAnalytics();
export const getDeliveryInsights = () => analyticsService.getDeliveryInsights();
export const getPatientDemographics = () => analyticsService.getPatientDemographics();
export const getRevenueData = () => analyticsService.getRevenueData();
export const getOrderStatusData = () => analyticsService.getOrderStatusData();
export const getMedicationCategories = () => analyticsService.getMedicationCategories();
export const getSatisfactionData = () => analyticsService.getSatisfactionData();
export const exportAnalyticsReport = () => analyticsService.exportAnalyticsReport();
